/**
 * AI helper banner: AI-authored suggestions for improving a draft before it is
 * posted.
 *
 * A pure presentation component built on `<Banner>` in the suggestion color:
 * an `<AITag>` disclosing the AI authorship, a short explanation, and a card
 * listing concrete rewriting tips. The caller owns the copy, so the same
 * banner serves whatever suggestions the moderation model produced.
 */

import { useTheme } from '@repo/theme';
import { AITag } from 'component/aitag';
import { Banner } from 'component/banner';
import { Heading, Paragraph } from 'component/text';
import { useTranslate } from 'component/translation';
import { labelToKey, type TLabel, t } from 'util/stdtypes';

import styles from './ai-helper-banner.module.css';

export type AiHelperBannerProps = {
    tips: TLabel[];
};

export function AiHelperBanner({ tips }: AiHelperBannerProps) {
    const { colors } = useTheme();
    const translate = useTranslate();

    return (
        <Banner color={colors.colorSurfaceBannerSuggestion}>
            <div className={styles.banner} data-testid='ai-helper-banner'>
                <AITag />
                <div className={styles.intro}>
                    <Heading color={colors.colorTextSecondaryOnBanner} label={t('ai-helper.suggestions_title', 'Suggestions')} />
                    <Paragraph
                        label={t(
                            'ai-helper.rephrase_message',
                            'Consider rephrasing your message to reach a wider audience and spark more meaningful discussions.',
                        )}
                    />
                </div>
                {tips?.length ? (
                    <div className={styles.tipsCard}>
                        <Paragraph label={t('ai-helper.tips_title', 'Try following these tips:')} strong type='large' />
                        <div className={styles.tipList}>
                            {tips.map((tip) => (
                                <Paragraph
                                    formatParams={{ tipText: translate(tip) }}
                                    key={labelToKey(tip)}
                                    label={t('ai-helper.bullet', '👉 {tipText}')}
                                    type='large'
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </Banner>
    );
}
